interface FilterCondition {
  filterType: string;
  type?: string;
  filter?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface FilterModel {
  [key: string]: {
    filterType: string;
    type?: string;
    filter?: string;
    values?: string[];
    dateFrom?: string;
    dateTo?: string;
    operator?: 'AND' | 'OR';
    conditions?: FilterCondition[];
  };
}

export const FIELD_MAPPING: Record<string, string> = {
  member_id: 'member_id',
  member_name: 'member_name',
  member_first_name: 'member_first_name',
  member_last_name: 'member_last_name',
  member_jurisdiction: 'member_jurisdiction',
  member_status: 'member_status',
  member_birthday: 'member_birthday',
  policy_carrier: 'policy_carrier',
  policy_plan: 'policy_plan',
  policy_priority_type: 'policy_priority_type',
  policy_provider_name: 'policy_provider_name',
  policy_created_at: 'policy_created_at',
  status: 'status',
  service_code: 'service_code',
  verified_by: 'verified_by',
  verified_at: 'verified_at',
  verified_through: 'verified_through',
  verified_via: 'verified_via',
  created_at: 'created_at'
};

export const INVERTED_SORT_FIELDS: Record<string, string> = {
  days_since_verified: 'verified_at'
};

export const addDaysYMD = (ymd: string, delta: number): string => {
  const [y, m, d] = ymd.split('-').map((v) => parseInt(v, 10));
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() + delta);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const translateSortModel = (
  sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>
): string | undefined => {
  if (!sortModel || sortModel.length === 0) return undefined;

  return sortModel
    .map((s) => {
      const inverted = INVERTED_SORT_FIELDS[s.colId];
      if (inverted) {
        return `${s.sort === 'desc' ? '' : '-'}${inverted}`;
      }

      const dir = s.sort === 'desc' ? '-' : '';
      const backend = FIELD_MAPPING[s.colId] || s.colId;
      return `${dir}${backend}`;
    })
    .join(',');
};

const DATE_TIME_PARAMS = ['verified_at', 'policy_created_at', 'created_at'].flatMap((field) => [
  `${field}_from`,
  `${field}_to`
]);

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const textValuesOf = (filter: FilterModel[string]): { type?: string; values: string[] } => {
  const conditions = filter.conditions?.length ? filter.conditions : [filter];

  return {
    type: conditions[0]?.type,
    values: conditions
      .map((condition) => condition.filter?.trim())
      .filter((value): value is string => !!value)
  };
};

export const translateFilterModel = (filterModel: FilterModel): Record<string, string> => {
  const params: Record<string, string> = {};

  Object.entries(filterModel).forEach(([colId, filter]) => {
    if (filter.filterType === 'text') {
      const { type, values } = textValuesOf(filter);
      if (values.length) params[type === 'equals' ? `${colId}_in` : colId] = values.join(',');
      return;
    }

    if (filter.filterType !== 'date') return;

    const from = filter.dateFrom?.slice(0, 10);
    const to = filter.dateTo?.slice(0, 10);

    if (filter.type === 'equals' && from) {
      params[`${colId}_from`] = from;
      params[`${colId}_to`] = addDaysYMD(from, 1);
    } else if (filter.type === 'greaterThan' && from) {
      params[`${colId}_from`] = addDaysYMD(from, 1);
    } else if (filter.type === 'lessThan' && from) {
      params[`${colId}_to`] = from;
    } else if (filter.type === 'inRange' && from && to) {
      params[`${colId}_from`] = from;
      params[`${colId}_to`] = addDaysYMD(to, 1);
    }
  });

  return params;
};

const localMidnight = (ymd: string): string => {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day).toISOString();
};

export const toRequestParams = (
  params: Record<string, string | string[]>
): Record<string, string | string[]> =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      DATE_TIME_PARAMS.includes(key) && typeof value === 'string' && YMD_PATTERN.test(value)
        ? localMidnight(value)
        : value
    ])
  );
